import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Link } from "react-router-dom";
import Header from "../components/header/Header";
import Footer from "../components/footer";
import Gotop from "../components/gotop";
import heroBackground from "../assets/images/hero-background.jpg";
import * as analytics from "../utils/analytics";
import "./Home.scss";
import "../components/contact/style.scss";

function Home() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchData, setSearchData] = useState({
    role: "",
    location: "",
  });
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const sliderRef = useRef(null);
  const handleSearch = (e) => {
    e.preventDefault();
    analytics.trackEvent("hero_search_submit", {
      search_term: searchData.role || undefined,
      location: searchData.location || undefined,
    });
    const queryParams = new URLSearchParams();
    if (searchData.role) queryParams.append("search", searchData.role);
    if (searchData.location) queryParams.append("location", searchData.location);
    navigate(`/careers?${queryParams.toString()}`);
  };

  const jobCategories = [
    {
      id: 1,
      title: "Software Engineering roles",
      color: "blue",
      buttonColor: "var(--primary)",
      image: require("../assets/images/blog/image-12.jpg"),
    },
    {
      id: 2,
      title: "AI and ML roles",
      color: "yellow",
      buttonColor: "#b06000",
      image: require("../assets/images/blog/image-13.jpg"),
    },
    {
      id: 3,
      title: "Cloud & DevOps roles",
      color: "red",
      buttonColor: "#d93025",
      image: require("../assets/images/blog/image-14.jpg"),
    },
    {
      id: 4,
      title: "Data Engineering roles",
      color: "green",
      buttonColor: "var(--secondary)",
      image: require("../assets/images/blog/image-01.jpg"),
    },
    {
      id: 5,
      title: "Account Management roles",
      color: "purple",
      buttonColor: "var(--secondary)",
      image: require("../assets/images/blog/image-02.jpg"),
    },
    {
      id: 6,
      title: "Full Stack Engineering roles",
      color: "blue",
      buttonColor: "var(--primary)",
      image: require("../assets/images/blog/image-03.jpg"),
    },
    {
      id: 7,
      title: "Virtual Assistant roles",
      color: "yellow",
      buttonColor: "#b06000",
      image: require("../assets/images/blog/image-04.jpg"),
    },
    {
      id: 8,
      title: "Product Management roles",
      color: "red",
      buttonColor: "#d93025",
      image: require("../assets/images/blog/image-05.jpg"),
    },
  ];

  const cardsToShow = 4; // Number of cards to show at once
  const maxSlide = Math.max(0, jobCategories.length - cardsToShow);

  const communityFeatures = [
    {
      id: 1,
      iconKey: "chat",
      title: "Tech & Career Discussions",
      description: "Join vibrant conversations with professionals worldwide. Share insights, ask questions, and connect with like-minded individuals in your field.",
    },
    {
      id: 2,
      iconKey: "book",
      title: "Interview & Resume Resources",
      description: "Access comprehensive guides, templates, and expert tips to ace your interviews and craft compelling resumes that stand out.",
    },
    {
      id: 3,
      iconKey: "globe",
      title: "Global Hiring Insights",
      description: "Stay informed with real-time trends, salary benchmarks, and market analysis from hiring managers across different regions.",
    },
    {
      id: 4,
      iconKey: "reviews",
      title: "Employer & Candidate Reviews",
      description: "Make informed decisions with authentic reviews and ratings from both employers and candidates about their experiences.",
    },
    {
      id: 5,
      iconKey: "graduation",
      title: "Skill Development & Hiring Readiness",
      description: "Enhance your expertise with curated learning paths, skill assessments, and personalized recommendations to boost your career.",
    },
  ];

  const renderCommunityIcon = (iconKey) => {
    const iconProps = { width: 28, height: 28, viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round" };
    switch (iconKey) {
      case "chat":
        return (
          <svg {...iconProps}><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg>
        );
      case "book":
        return (
          <svg {...iconProps}><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" /><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" /><path d="M8 7h8M8 11h8" /></svg>
        );
      case "globe":
        return (
          <svg {...iconProps}><circle cx="12" cy="12" r="10" /><path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" /></svg>
        );
      case "reviews":
        return (
          <svg {...iconProps}><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" /></svg>
        );
      case "graduation":
        return (
          <svg {...iconProps}><path d="M22 10v6M2 10l10-5 10 5-10 5z" /><path d="M6 12v5c0 2 2 3 6 3s6-1 6-3v-5" /></svg>
        );
      default:
        return null;
    }
  };

  // Auto-play functionality for job categories
  useEffect(() => {
    if (!isAutoPlaying) return;

    const interval = setInterval(() => {
      setCurrentSlide((prev) => {
        if (prev >= maxSlide) {
          return 0; // Loop back to start
        }
        return prev + 1;
      });
    }, 3000); // Change slide every 3 seconds

    return () => clearInterval(interval);
  }, [isAutoPlaying, maxSlide]);

  const handlePrevSlide = () => {
    analytics.trackEvent("carousel_nav", { direction: "prev" });
    setIsAutoPlaying(false);
    setCurrentSlide((prev) => (prev <= 0 ? maxSlide : prev - 1));
  };

  const handleNextSlide = () => {
    analytics.trackEvent("carousel_nav", { direction: "next" });
    setIsAutoPlaying(false);
    setCurrentSlide((prev) => (prev >= maxSlide ? 0 : prev + 1));
  };

  const handleCardClick = (category) => {
    analytics.trackEvent("job_category_click", { category_title: category.title });
    navigate("/careers");
  };

  // Scroll to section if hash is present in URL or if navigating from another page
  useEffect(() => {
    if (location.hash === '#what-is-talsolution') {
      setTimeout(() => {
        const section = document.getElementById('what-is-talsolution');
        if (section) {
          section.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    } else if (location.hash === '#contact-section') {
      setTimeout(() => {
        const section = document.getElementById('contact-section');
        if (section) {
          section.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  }, [location]);

  useEffect(() => {
    document.title = "Talsolution – Connect Global Talent with US Opportunities";
    return () => {
      document.title = "Talsolution";
    };
  }, []);

  return (
    <>
      <Header />

      {/* Hero Section with Search */}
      <section className="hero-search-section" style={{ backgroundImage: `url(${heroBackground})` }}>
        <div className="hero-overlay"></div>
        <div className="tf-container">
          <div className="hero-content">
            <h1 className="hero-title">
              Connect Global Talent with US Opportunities
            </h1>
            <p className="hero-subtitle">
              Talsolution is a free hiring platform where US companies connect with skilled global professionals — including 
              software engineers, IT specialists, and virtual assistants — for contract and full-time roles.
            </p>
            
            <form className="hero-search-form" onSubmit={handleSearch}>
              <div className="search-form-wrapper">
                <div className="search-input-group">
                  <label htmlFor="role">Role</label>
                  <input
                    id="role"
                    type="text"
                    placeholder="Software engineer, Data scientist, DevOps..."
                    value={searchData.role}
                    onChange={(e) => setSearchData({ ...searchData, role: e.target.value })}
                  />
                </div>

                <div className="search-input-group">
                  <label htmlFor="location">Where?</label>
                  <input
                    id="location"
                    type="text"
                    placeholder="New York, London, Remote..."
                    value={searchData.location}
                    onChange={(e) => setSearchData({ ...searchData, location: e.target.value })}
                  />
                </div>

                <button type="submit" className="search-submit-btn">
                  Search
                </button>
              </div>
            </form>

            <div className="popular-searches">
              <span className="popular-label">Popular roles:</span>
              <button onClick={() => { analytics.trackEvent("popular_role_click", { role: ".NET Engineer" }); setSearchData({ ...searchData, role: ".NET Engineer" }); }}>
                .NET Engineer
              </button>
              <button onClick={() => { analytics.trackEvent("popular_role_click", { role: "Data Engineer" }); setSearchData({ ...searchData, role: "Data Engineer" }); }}>
                Data Engineer
              </button>
              <button onClick={() => { analytics.trackEvent("popular_role_click", { role: "Cloud Architect" }); setSearchData({ ...searchData, role: "Cloud Architect" }); }}>
                Cloud Architect
              </button>
              <button onClick={() => { analytics.trackEvent("popular_role_click", { role: "AI Engineer" }); setSearchData({ ...searchData, role: "AI Engineer" }); }}>
                AI Engineer
              </button>
            </div>

            {/* Trust Signals */}
            <div className="trust-signals">
              <div className="trust-item">
                <svg className="trust-icon" width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                  <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                </svg>
                <span className="trust-text">Global Talent Pool</span>
              </div>
              <div className="trust-item">
                <i className="icon-map-pin trust-icon"></i>
                <span className="trust-text">US-Based Hiring Opportunities</span>
              </div>
              <div className="trust-item">
                <i className="icon-work trust-icon"></i>
                <span className="trust-text">Contract & Full-Time Roles</span>
              </div>
              <div className="trust-item">
                <svg className="trust-icon" width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                  <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                  <text x="12" y="17.5" textAnchor="middle" fontSize="6" fontWeight="bold" fill="currentColor">100%</text>
                </svg>
                <span className="trust-text">Free for Candidates</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Job Categories Carousel Section */}
      <section className="job-categories-section">
        <div className="tf-container">
          <div className="job-categories-section-header">
            <h2 className="job-categories-section-title">Job Categories</h2>
            <button
              className="job-categories-see-all-btn"
              onClick={() => {
                analytics.trackEvent("cta_click", { cta_name: "see_jobs_hero", destination: "/careers" });
                navigate("/careers");
              }}
            >
              See jobs <i className="icon-arrow-right2"></i>
            </button>
          </div>
          <div className="categories-carousel-wrapper">
            <button 
              className="carousel-arrow carousel-arrow-left" 
              onClick={handlePrevSlide}
              aria-label="Previous"
            >
              <i className="icon-keyboard_arrow_left"></i>
            </button>

            <div 
              className="categories-carousel"
              ref={sliderRef}
              onMouseEnter={() => setIsAutoPlaying(false)}
              onMouseLeave={() => setIsAutoPlaying(true)}
            >
              <div 
                className="categories-track"
                style={{
                  transform: `translateX(-${currentSlide * (100 / cardsToShow)}%)`,
                }}
              >
                {jobCategories.map((category) => (
                  <div
                    key={category.id}
                    className={`category-card-carousel category-${category.color}`}
                    onClick={() => handleCardClick(category)}
                  >
                    <div className="category-image-top">
                      <img src={category.image} alt={category.title} />
                    </div>
                    <div className="category-content-new">
                      <h3>{category.title}</h3>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <button 
              className="carousel-arrow carousel-arrow-right" 
              onClick={handleNextSlide}
              aria-label="Next"
            >
              <i className="icon-keyboard_arrow_right"></i>
            </button>
          </div>

          {/* Carousel Indicators */}
          <div className="carousel-indicators">
            {Array.from({ length: maxSlide + 1 }).map((_, index) => (
              <button
                key={index}
                className={`indicator ${currentSlide === index ? 'active' : ''}`}
                onClick={() => {
                  analytics.trackEvent("carousel_indicator", { slide_index: index });
                  setCurrentSlide(index);
                  setIsAutoPlaying(false);
                }}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* What Talsolution Does */}
      <section id="what-is-talsolution" className="what-talsolution-section">
        <div className="tf-container">
          <div className="section-header">
            <h2 className="section-title">What Is Talsolution?</h2>
            <p className="section-description">
              Talsolution is a hiring and talent network built to simplify how US companies find and work with global professionals.
              We focus on tech hiring, remote roles, and virtual assistance, helping companies access world-class talent beyond borders — 
              while giving professionals fair access to US opportunities.
            </p>
          </div>

          <div className="core-pillars">
            <div className="pillar-card">
              <div className="pillar-icon">🔹</div>
              <h3>For Candidates</h3>
              <ul>
                <li>Access verified US job opportunities</li>
                <li>Apply directly with no fees</li>
                <li>Get exposure to real hiring managers</li>
                <li>Build a global professional profile</li>
              </ul>
            </div>

            <div className="pillar-card">
              <div className="pillar-icon">🔹</div>
              <h3>For Companies</h3>
              <ul>
                <li>Hire global talent without expensive recruiters</li>
                <li>Access pre-screened international professionals</li>
                <li>Flexible hiring: contract, remote, or full-time</li>
                <li>Faster hiring decisions</li>
              </ul>
            </div>

            <div className="pillar-card">
              <div className="pillar-icon">🔹</div>
              <h3>For Talsolution</h3>
              <ul>
                <li>Curated job postings</li>
                <li>Centralized candidate evaluation</li>
                <li>Reduced hiring risk</li>
                <li>Long-term talent network</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Why Talsolution Is Free */}
      <section className="why-free-section">
        <div className="tf-container">
          <div className="why-free-content">
            <h2 className="section-title">Why Talsolution Is 100% Free for Candidates</h2>
            <p className="section-description">
              We believe access to global opportunities should not be limited by geography or financial barriers.
              Talsolution was created to bridge the gap between US companies and skilled professionals worldwide — 
              without charging candidates or taking commissions from their salaries.
            </p>

            <div className="reasons-grid">
              <div className="reason-card">
                <h4>To help global professionals access US opportunities</h4>
                <p>Many skilled professionals outside the US struggle to find legitimate, well-paying remote roles. Talsolution exists to change that.</p>
              </div>

              <div className="reason-card">
                <h4>To help US companies hire better, faster</h4>
                <p>Companies benefit from a trusted talent pool without inflated recruitment fees or middlemen.</p>
              </div>

              <div className="reason-card">
                <h4>To build a long-term global talent network</h4>
                <p>Our goal is not transactions — it's relationships, trust, and long-term collaboration.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Who Can Use Talsolution */}
      <section className="who-can-use-section">
        <div className="tf-container">
          <div className="who-can-use-header">
            <h2 className="section-title">
              Who Can Use Talsolution?
            </h2>
            <p className="section-description">
              Connecting world-class talent with forward-thinking companies. Whether you're building the future or scaling your operations, we have the perfect match.
            </p>
          </div>

          <div className="user-categories">
            <div className="user-category-card">
              <div
                className="card-bg"
                style={{ backgroundImage: `url(${require("../assets/images/blog/image-24.jpg")})` }}
              />
              <div className="card-overlay" />
              <div className="card-content">
                <div className="category-icon-wrap">
                  <svg className="category-icon-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="16 18 22 12 16 6" />
                    <polyline points="8 6 2 12 8 18" />
                    <path d="M12 6v12" />
                  </svg>
                </div>
                <h3>Engineers & Developers</h3>
                <ul>
                  <li>Full-Stack Developers</li>
                  <li>DevOps Engineers</li>
                  <li>QA Specialists</li>
                  <li>Mobile Developers</li>
                  <li>Data Scientists</li>
                </ul>
              </div>
            </div>

            <div className="user-category-card">
              <div
                className="card-bg"
                style={{ backgroundImage: `url(${require("../assets/images/blog/image-26.webp")})` }}
              />
              <div className="card-overlay" />
              <div className="card-content">
                <div className="category-icon-wrap category-icon-wrap--circle">
                  <svg className="category-icon-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 18v-6a9 9 0 0 1 18 0v6" />
                    <path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3zM3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z" />
                  </svg>
                </div>
                <h3>Virtual Assistants</h3>
                <ul>
                  <li>Executive Assistants</li>
                  <li>Customer Support</li>
                  <li>Data Entry Experts</li>
                  <li>Social Media Managers</li>
                  <li>Bookkeepers</li>
                </ul>
              </div>
            </div>

            <div className="user-category-card">
              <div
                className="card-bg"
                style={{ backgroundImage: `url(${require("../assets/images/blog/image-25.jpeg")})` }}
              />
              <div className="card-overlay" />
              <div className="card-content">
                <div className="category-icon-wrap">
                  <svg className="category-icon-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="3" width="18" height="18" rx="2" />
                    <path d="M3 9h18M9 21V9M15 21V9" />
                  </svg>
                </div>
                <h3>US Companies</h3>
                <ul>
                  <li>Startups</li>
                  <li>SMBs</li>
                  <li>Remote-first companies</li>
                  <li>Agencies looking for offshore talent</li>
                  <li>Enterprise teams</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="how-it-works-section">
        <div className="tf-container">
          <h2 className="section-title">How Talsolution Works</h2>
          <p className="section-description">
            A simple, transparent process that connects global talent with US opportunities
          </p>

          <div className="workflow-steps">
            <div className="workflow-step">
              <div className="step-number">1</div>
              <div className="step-content">
                <h3>Admin posts verified job opportunities</h3>
                <p>Real positions from US companies, vetted and verified</p>
              </div>
            </div>

            <div className="workflow-step">
              <div className="step-number">2</div>
              <div className="step-content">
                <h3>Candidates search and apply for free</h3>
                <p>Browse opportunities and submit your application at no cost</p>
              </div>
            </div>

            <div className="workflow-step">
              <div className="step-number">3</div>
              <div className="step-content">
                <h3>Talsolution reviews and evaluates applicants</h3>
                <p>We assess qualifications and match candidates to opportunities</p>
              </div>
            </div>

            <div className="workflow-step">
              <div className="step-number">4</div>
              <div className="step-content">
                <h3>Shortlisted candidates are shared with clients</h3>
                <p>Top candidates are presented to hiring companies</p>
              </div>
            </div>

            <div className="workflow-step">
              <div className="step-number">5</div>
              <div className="step-content">
                <h3>Hiring decision & onboarding</h3>
                <p>Companies make their choice and successful candidates begin their journey</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Community & Network */}
      <section className="community-section">
        <div className="tf-container">
          <div className="community-content">
            <h2 className="section-title">More Than Jobs — A Professional Network</h2>
            <p className="section-description">
              Talsolution is evolving beyond job listings. We're building a community-driven platform where professionals
              can learn, share, and grow together.
            </p>

            <div className="community-cards-grid">
              {communityFeatures.map((feature) => (
                <div key={feature.id} className="community-network-card">
                  <div className="community-card-icon-wrap">
                    {renderCommunityIcon(feature.iconKey)}
                  </div>
                  <h4 className="community-card-title">{feature.title}</h4>
                  <p className="community-card-description">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Differentiation */}
      <section className="differentiation-section">
        <div className="tf-container">
          <h2 className="section-title">How Talsolution Is Different</h2>
          
          <div className="comparison-table">
            <div className="comparison-header">
              <div className="col-platform">Platform</div>
              <div className="col-type">Type</div>
              <div className="col-price">Price</div>
              <div className="col-signin">Login Required</div>
              <div className="col-screening">Pre-screening</div>
              <div className="col-direct">Direct Hiring</div>
            </div>
            
            <div className="comparison-row">
              <div className="col-platform">
                <img src={require("../assets/images/logos/dice.png")} alt="Dice" className="platform-logo" />
                <span>Dice</span>
              </div>
              <div className="col-type">Job board</div>
              <div className="col-price">Free / Paid</div>
              <div className="col-signin">Yes</div>
              <div className="col-screening">No</div>
              <div className="col-direct">No</div>
            </div>
            
            <div className="comparison-row">
              <div className="col-platform">
                <img src={require("../assets/images/logos/linkedin.png")} alt="LinkedIn" className="platform-logo" />
                <span>LinkedIn</span>
              </div>
              <div className="col-type">Professional network</div>
              <div className="col-price">Free / Premium</div>
              <div className="col-signin">Yes</div>
              <div className="col-screening">No</div>
              <div className="col-direct">Limited</div>
            </div>
            
            <div className="comparison-row">
              <div className="col-platform">
                <img src={require("../assets/images/logos/glassdoor.png")} alt="Glassdoor" className="platform-logo" />
                <span>Glassdoor</span>
              </div>
              <div className="col-type">Company reviews</div>
              <div className="col-price">Free</div>
              <div className="col-signin">Yes</div>
              <div className="col-screening">No</div>
              <div className="col-direct">No</div>
            </div>
            
            <div className="comparison-row">
              <div className="col-platform">
                <img src={require("../assets/images/logos/indeed.png")} alt="Indeed" className="platform-logo" />
                <span>Indeed</span>
              </div>
              <div className="col-type">Job aggregator</div>
              <div className="col-price">Free</div>
              <div className="col-signin">Optional</div>
              <div className="col-screening">No</div>
              <div className="col-direct">No</div>
            </div>
            
            <div className="comparison-row">
              <div className="col-platform">
                <img src={require("../assets/images/logos/upwork.png")} alt="Upwork" className="platform-logo" />
                <span>Upwork</span>
              </div>
              <div className="col-type">Freelance marketplace</div>
              <div className="col-price">Commission fees</div>
              <div className="col-signin">Yes</div>
              <div className="col-screening">Basic</div>
              <div className="col-direct">Yes</div>
            </div>
            
            <div className="comparison-row">
              <div className="col-platform">
                <img src={require("../assets/images/logos/hubstafftalent.png")} alt="Hubstaff Talent" className="platform-logo" />
                <span>Hubstaff Talent</span>
              </div>
              <div className="col-type">Freelancer directory</div>
              <div className="col-price">Free</div>
              <div className="col-signin">Yes</div>
              <div className="col-screening">No</div>
              <div className="col-direct">Yes</div>
            </div>
            
            <div className="comparison-row highlight">
              <div className="col-platform">
                <img src={require("../assets/images/logos/logo.png")} alt="Talsolution" className="platform-logo" />
                <span>Talsolution</span>
              </div>
              <div className="col-type">Curated network + agency</div>
              <div className="col-price">100% Free</div>
              <div className="col-signin">No</div>
              <div className="col-screening">Yes ✓</div>
              <div className="col-direct">Yes ✓</div>
            </div>
          </div>

          <div className="differentiation-message">
            <p>
              <strong>Key difference:</strong> We don't just list jobs. We help make hiring decisions easier.
            </p>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="final-cta-section">
        <div className="tf-container">
          <div className="cta-content">
            <h2 className="cta-title">Join the Talsolution Network</h2>
            <p className="cta-description">
              Start your journey to global opportunities today. Free for candidates, trusted by companies.
            </p>
            <div className="cta-buttons">
              <button className="cta-btn secondary" onClick={() => { analytics.trackEvent("cta_click", { cta_name: "search_open_jobs", destination: "/careers" }); navigate("/careers"); }}>
                Search Open Jobs
              </button>
              <button className="cta-btn primary" onClick={() => { analytics.trackEvent("cta_click", { cta_name: "create_profile", destination: "/register" }); navigate("/register"); }}>
                Create Your Free Profile
              </button>
            </div>
            <p className="cta-trust-line">
              Track applications. Message employers. Always free for candidates.
            </p>
            <p className="cta-trust-line-small">
              Free for candidates. No salary cuts. No hidden fees.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact-section" className="contact-section">
        <div className="tf-container">
          <div className="contact-section-header">
            <h2 className="contact-section-title">Contact Us</h2>
            <p className="contact-section-description">
              Have questions? We're here to help. Reach out to us through the form below or connect with us directly. 
              Whether you're a candidate looking for opportunities or a company seeking global talent, we're ready to assist you.
            </p>
          </div>
          <div className="row">
            <div className="col-lg-12">
              <div className="contact-wrapper">
                <div className="contact-info-card-combined">
                  <div className="contact-info-item">
                    <div className="contact-icon">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                      </svg>
                    </div>
                    <div className="contact-info-content">
                      <h3>Call Us</h3>
                      <p><a href="tel:+12108302282">+1 (210) 830-2282</a></p>
                    </div>
                  </div>
                  <div className="contact-info-item">
                    <div className="contact-icon">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                        <polyline points="22,6 12,13 2,6" />
                      </svg>
                    </div>
                    <div className="contact-info-content">
                      <h3>Email</h3>
                      <p><a href="mailto:info@talsolution.com">info@talsolution.com</a></p>
                    </div>
                  </div>
                  <div className="contact-info-item">
                    <div className="contact-icon">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                        <circle cx="12" cy="10" r="3" />
                      </svg>
                    </div>
                    <div className="contact-info-content">
                      <h3>Address</h3>
                      <p>2821 Mexico Gravel Rd #5<br />Columbia MO 65202</p>
                    </div>
                  </div>
                </div>
                <div className="contact-form-wrapper">
                  <form
                    className="contact-form-modern"
                    onSubmit={(e) => {
                      e.preventDefault();
                      analytics.trackEvent("contact_form_submit", { form_name: "home_contact" });
                      // Submit logic (e.g. API call) can be added here
                    }}
                  >
                    <div className="form-row">
                      <div className="form-group">
                        <input type="text" placeholder="Your Name" required />
                      </div>
                      <div className="form-group">
                        <input type="text" placeholder="Subject" required />
                      </div>
                    </div>
                    <div className="form-group">
                      <input type="email" placeholder="Your Email" required />
                    </div>
                    <div className="form-group">
                      <textarea placeholder="Your Message" rows="6" required></textarea>
                    </div>
                    <button type="submit" className="contact-submit-btn">
                      Send Message
                    </button>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <Gotop />
    </>
  );
}

export default Home;
